import React, { useState, useEffect } from 'react';
import './FilterBar.css';

const FilterBar = ({ onFiltersChange, currentFilters, availableFilters = { devices: [], trafficSources: [], geos: [] } }) => {
  const [showWebDropdown, setShowWebDropdown] = useState(false);
  const [showSocialDropdown, setShowSocialDropdown] = useState(false);
  const [showAIDropdown, setShowAIDropdown] = useState(false);
  const [showDeviceDropdown, setShowDeviceDropdown] = useState(false);
  const [showGeoDropdown, setShowGeoDropdown] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true); // Start collapsed on mobile
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      // Auto-collapse on mobile, auto-expand on desktop
      if (window.innerWidth <= 768) {
        setIsCollapsed(true);
      } else {
        setIsCollapsed(false);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Filter options based on available data
  const webOptions = [
    { value: 'all', label: 'All Sources', icon: '🌐' },
    { value: 'direct', label: 'Direct', icon: '🔗' },
    { value: 'organic', label: 'Organic', icon: '🔍' },
    { value: 'paid', label: 'Paid', icon: '💰' },
    { value: 'referral', label: 'Referral', icon: '🔗' },
    { value: 'Email', label: 'Email', icon: '📧' }
  ].filter(option => 
    option.value === 'all' || availableFilters.trafficSources.includes(option.value)
  );

  const socialOptions = [
    { value: 'Social', label: 'All Social', icon: '📱' },
    { value: 'facebook', label: 'Facebook', icon: '📘' },
    { value: 'twitter', label: 'Twitter/X', icon: '🐦' },
    { value: 'reddit', label: 'Reddit', icon: '🔴' },
    { value: 'youtube', label: 'YouTube', icon: '📺' },
    { value: 'tiktok', label: 'TikTok', icon: '🎵' },
    { value: 'instagram', label: 'Instagram', icon: '📷' },
    { value: 'linkedin', label: 'LinkedIn', icon: '💼' },
    { value: 'bluesky', label: 'Bluesky', icon: '☁️' }
  ].filter(option => 
    option.value === 'Social' || availableFilters.trafficSources.includes(option.value)
  );

  const aiOptions = [
    { value: 'AI', label: 'All AI', icon: '🤖' },
    { value: 'chatgpt', label: 'ChatGPT', icon: '💬' },
    { value: 'claude', label: 'Claude', icon: '🧠' },
    { value: 'perplexity', label: 'Perplexity', icon: '🔍' },
    { value: 'gemini', label: 'Gemini', icon: '⭐' },
    { value: 'bing', label: 'Bing Chat', icon: '🔎' },
    { value: 'poe', label: 'Poe', icon: '📝' },
    { value: 'characterai', label: 'Character.AI', icon: '👤' },
    { value: 'huggingface', label: 'Hugging Face', icon: '🤗' }
  ].filter(option => 
    option.value === 'AI' || availableFilters.trafficSources.includes(option.value)
  );

  const deviceOptions = [
    { value: 'all', label: 'All Devices', icon: '📱💻📟' },
    { value: 'desktop', label: 'Desktop', icon: '💻' },
    { value: 'mobile', label: 'Mobile', icon: '📱' },
    { value: 'tablet', label: 'Tablet', icon: '📟' }
  ].filter(option => 
    option.value === 'all' || availableFilters.devices.includes(option.value)
  );

  const handleFilterChange = (filterType, value) => {
    const newFilters = {
      ...currentFilters,
      [filterType]: value
    };
    onFiltersChange(newFilters);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (currentFilters.device !== 'all') count++;
    if (currentFilters.trafficSource !== 'all') count++;
    if (currentFilters.geo && currentFilters.geo !== 'all') count++;
    return count;
  };

  const clearFilters = () => {
    onFiltersChange({ device: 'all', trafficSource: 'all', geo: 'all' });
    setShowWebDropdown(false);
    setShowSocialDropdown(false);
    setShowDeviceDropdown(false);
  };

  const getCurrentWebLabel = () => {
    const option = webOptions.find(opt => opt.value === currentFilters.trafficSource);
    return option ? option.label : 'Source';
  };

  const getCurrentSocialLabel = () => {
    const option = socialOptions.find(opt => opt.value === currentFilters.trafficSource);
    return option ? option.label : 'Social';
  };

  const getCurrentDeviceLabel = () => {
    const option = deviceOptions.find(opt => opt.value === currentFilters.device);
    return option ? option.label : 'Device';
  };

  const isWebSource = () => {
    return ['direct', 'organic', 'paid', 'referral', 'Email'].includes(currentFilters.trafficSource);
  };

  const isSocialSource = () => {
    return ['Social', 'facebook', 'twitter', 'reddit', 'youtube', 'tiktok', 'instagram', 'linkedin', 'bluesky'].includes(currentFilters.trafficSource);
  };

  const isAISource = () => {
    return ['AI', 'chatgpt', 'claude', 'perplexity', 'gemini', 'bing', 'poe', 'characterai', 'huggingface'].includes(currentFilters.trafficSource);
  };

  const getCurrentAILabel = () => {
    const option = aiOptions.find(opt => opt.value === currentFilters.trafficSource);
    return option ? option.label : 'AI';
  };

  // Close other dropdowns when one opens
  const handleDropdownToggle = (dropdownType) => {
    setShowWebDropdown(dropdownType === 'web' ? !showWebDropdown : false);
    setShowSocialDropdown(dropdownType === 'social' ? !showSocialDropdown : false);
    setShowAIDropdown(dropdownType === 'ai' ? !showAIDropdown : false);
    setShowDeviceDropdown(dropdownType === 'device' ? !showDeviceDropdown : false);
    setShowGeoDropdown(dropdownType === 'geo' ? !showGeoDropdown : false);
  };

  return (
    <div className="filter-bar-compact">
      {/* Mobile Toggle Button */}
      {isMobile && (
        <button 
          className="filter-toggle-button"
          onClick={() => setIsCollapsed(!isCollapsed)}
          aria-label={isCollapsed ? 'Show filters' : 'Hide filters'}
        >
          <span className={`filter-icon filter-toggle-icon ${!isCollapsed ? 'filter-toggle-icon-open' : ''}`}>▼</span>
          <span>{isCollapsed ? 'Show Filters' : 'Hide Filters'}</span>
          {getActiveFiltersCount() > 0 && (
            <span className="filter-badge">{getActiveFiltersCount()}</span>
          )}
        </button>
      )}

      {/* Filter Content - hidden when collapsed on mobile */}
      <div className={`filter-content ${isMobile && isCollapsed ? 'filter-collapsed' : ''}`}>
        <div className="filter-dropdown-group">
          {!isMobile && <span className="filter-label">Filters:</span>}
        
        {/* Source Dropdown - only show if there are source options with data */}
        {webOptions.length > 1 && (
          <div className="filter-dropdown">
            <button 
              className={`filter-dropdown-button ${isWebSource() ? 'active' : ''}`}
              onClick={() => handleDropdownToggle('web')}
            >
              <span className="filter-icon">🌐</span>
              {isWebSource() ? getCurrentWebLabel() : 'Source'}
              <span className="dropdown-arrow">▼</span>
            </button>
            {showWebDropdown && (
              <div className="filter-dropdown-menu">
                {webOptions.map(option => (
                  <button
                    key={option.value}
                    className={`filter-dropdown-item ${currentFilters.trafficSource === option.value ? 'active' : ''}`}
                    onClick={() => {
                      handleFilterChange('trafficSource', option.value);
                      setShowWebDropdown(false);
                    }}
                  >
                    <span className="filter-icon">{option.icon}</span>
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Social Dropdown - only show if there are social options with data */}
        {socialOptions.length > 1 && (
          <div className="filter-dropdown">
            <button 
              className={`filter-dropdown-button ${isSocialSource() ? 'active' : ''}`}
              onClick={() => handleDropdownToggle('social')}
            >
              <span className="filter-icon">📱</span>
              {isSocialSource() ? getCurrentSocialLabel() : 'Social'}
              <span className="dropdown-arrow">▼</span>
            </button>
            {showSocialDropdown && (
              <div className="filter-dropdown-menu">
                {socialOptions.map(option => (
                  <button
                    key={option.value}
                    className={`filter-dropdown-item ${currentFilters.trafficSource === option.value ? 'active' : ''}`}
                    onClick={() => {
                      handleFilterChange('trafficSource', option.value);
                      setShowSocialDropdown(false);
                    }}
                  >
                    <span className="filter-icon">{option.icon}</span>
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* AI Dropdown - only show if there are AI options with data */}
        {aiOptions.length > 1 && (
          <div className="filter-dropdown">
            <button 
              className={`filter-dropdown-button ${isAISource() ? 'active' : ''}`}
              onClick={() => handleDropdownToggle('ai')}
            >
              <span className="filter-icon">🤖</span>
              {isAISource() ? getCurrentAILabel() : 'AI'}
              <span className="dropdown-arrow">▼</span>
            </button>
            {showAIDropdown && (
              <div className="filter-dropdown-menu">
                {aiOptions.map(option => (
                  <button
                    key={option.value}
                    className={`filter-dropdown-item ${currentFilters.trafficSource === option.value ? 'active' : ''}`}
                    onClick={() => {
                      handleFilterChange('trafficSource', option.value);
                      setShowAIDropdown(false);
                    }}
                  >
                    <span className="filter-icon">{option.icon}</span>
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}


        {/* Device Dropdown */}
        <div className="filter-dropdown">
          <button 
            className={`filter-dropdown-button ${currentFilters.device !== 'all' ? 'active' : ''}`}
            onClick={() => handleDropdownToggle('device')}
          >
            <span className="filter-icon">📱💻</span>
            <span className="filter-text">{getCurrentDeviceLabel()}</span>
            <span className="dropdown-arrow">▼</span>
          </button>
          {showDeviceDropdown && (
            <div className="filter-dropdown-menu">
              {deviceOptions.map(option => (
                <button
                  key={option.value}
                  className={`filter-dropdown-item ${currentFilters.device === option.value ? 'active' : ''}`}
                  onClick={() => {
                    handleFilterChange('device', option.value);
                    setShowDeviceDropdown(false);
                  }}
                >
                  <span className="filter-icon">{option.icon}</span>
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Geo Dropdown - only show if there are geo options with data */}
        {availableFilters.geos.length > 0 && (
          <div className="filter-dropdown">
            <button 
              className={`filter-dropdown-button ${currentFilters.geo && currentFilters.geo !== 'all' ? 'active' : ''}`}
              onClick={() => handleDropdownToggle('geo')}
            >
              <span className="filter-icon">🗺️</span>
              {currentFilters.geo && currentFilters.geo !== 'all' ? currentFilters.geo : 'Geo'}
              <span className="dropdown-arrow">▼</span>
            </button>
            {showGeoDropdown && (
              <div className="filter-dropdown-menu">
                <input
                  type="text"
                  placeholder="Search country..."
                  className="filter-text-input"
                  value={currentFilters.geo === 'all' ? '' : (currentFilters.geo || '')}
                  onChange={(e) => handleFilterChange('geo', e.target.value || 'all')}
                  style={{ width: '100%', padding: '0.5rem', marginBottom: '0.5rem', borderRadius: '4px', border: '1px solid #e5e7eb' }}
                  autoFocus
                />
                <button
                  className={`filter-dropdown-item ${currentFilters.geo === 'all' ? 'active' : ''}`}
                  onClick={() => {
                    handleFilterChange('geo', 'all');
                    setShowGeoDropdown(false);
                  }}
                >
                  <span className="filter-icon">🌍</span>
                  All Countries
                </button>
                {availableFilters.geos.map(country => (
                  <button
                    key={country}
                    className={`filter-dropdown-item ${currentFilters.geo === country ? 'active' : ''}`}
                    onClick={() => {
                      handleFilterChange('geo', country);
                      setShowGeoDropdown(false);
                    }}
                  >
                    <span className="filter-icon">🌍</span>
                    {country}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

        {getActiveFiltersCount() > 0 && (
          <button className="clear-filters" onClick={clearFilters}>
            Clear All
          </button>
        )}
      </div>
    </div>
  );
};

export default FilterBar;